import { collections, sections } from '../lib/contentConfig';
import { diffBundles } from '../lib/diff';
import type { Bundle } from '../lib/validate';

const titles: Record<string, string> = Object.fromEntries(
  [...Object.entries(collections), ...Object.entries(sections)].map(([k, v]) => [k, v.title]),
);

const MAX_PATHS = 6;

const paths = (ps: string[]) =>
  ps.length <= MAX_PATHS
    ? ps.join(', ')
    : `${ps.slice(0, MAX_PATHS).join(', ')} +${ps.length - MAX_PATHS} more`;

/** A per-entry summary of what publishing the draft would change: added, removed, changed (with fields), reordered. */
export function DraftDiff({ published, draft }: { published: Bundle; draft: Bundle }) {
  const diff = diffBundles(published, draft);
  if (diff.length === 0)
    return (
      <p className="text-sm text-bruise">No changes: the draft matches the published version.</p>
    );

  return (
    <ul className="mt-2 space-y-3 text-sm">
      {diff.map((d) => (
        <li key={d.section}>
          <h3 className="font-semibold">{titles[d.section] ?? d.section}</h3>
          {d.kind === 'section' ? (
            <p>
              <span className="font-semibold">Changed:</span>{' '}
              <span className="font-mono text-xs">{paths(d.paths)}</span>
            </p>
          ) : (
            <ul className="pl-4">
              {d.added.map((id) => (
                <li key={`a-${id}`}>
                  <span className="font-semibold text-benday">+ Added</span>{' '}
                  <span className="font-mono">{id}</span>
                </li>
              ))}
              {d.removed.map((id) => (
                <li key={`r-${id}`}>
                  <span className="font-semibold text-blam">− Removed</span>{' '}
                  <span className="font-mono">{id}</span>
                </li>
              ))}
              {d.changed.map((c) => (
                <li key={`c-${c.id}`}>
                  <span className="font-semibold">~ Changed</span>{' '}
                  <span className="font-mono">{c.id}</span>
                  <span className="text-bruise">: </span>
                  <span className="font-mono text-xs text-bruise">{paths(c.paths)}</span>
                </li>
              ))}
              {d.reordered && <li className="text-bruise">↕ Order changed</li>}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}
